/**
 * ProposalItemsTable — presentational table for a proposal's items.
 *
 * Extracted from ProposalEditor (PR 0b) to keep ProposalEditor below
 * 500 lines and split orchestration from presentation. All state lives
 * in the parent; this component is purely controlled.
 */
import React from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ProductSearchInput, {
  Product as CatalogProduct,
  COMPONENT_TYPE_TO_ROOT_CATEGORY_ID,
} from "../common/ProductSearchInput";
import { ProposalDraftItem, DriftItem } from "../../model/Proposal";
import DriftChip from "./DriftChip";

function formatCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

interface Props {
  items: ProposalDraftItem[];
  searchValues: Record<string, string>;
  setSearchValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  updateItem: (localId: string, patch: Partial<ProposalDraftItem>) => void;
  removeItem: (localId: string) => void;
  addItem: () => void;
  /** Optional drift items; when present, shows DriftChip inline per item row */
  driftItems?: DriftItem[];
  /** Called when user clicks a DriftChip (opens drift drawer) */
  onDriftClick?: () => void;
  /** When false (comercial), hides the cost column — RNF2 server-mirrors client. */
  isAdmin?: boolean;
}

const ProposalItemsTable: React.FC<Props> = ({
  items,
  searchValues,
  setSearchValues,
  updateItem,
  removeItem,
  addItem,
  driftItems,
  onDriftClick,
  isAdmin = false,
}) => {
  const totalRev4 = items.reduce(
    (sum, i) => sum + (i.rev4_cents ?? 0) * i.quantity,
    0,
  );
  const totalPvp = items.reduce((sum, i) => sum + i.pvp_cents * i.quantity, 0);
  const totalCost = items.reduce(
    (sum, i) => sum + i.cost_cents * i.quantity,
    0,
  );
  const hasEmptyRow = items.some((i) => !i.product_id);

  return (
    <>
      <Table size="small" sx={{ mb: 1 }}>
        <TableHead>
          <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "#f5f5f5" } }}>
            <TableCell sx={{ width: 160 }}>Tipo (slot)</TableCell>
            <TableCell sx={{ minWidth: 220 }}>Produto</TableCell>
            <TableCell align="right" sx={{ width: 70 }}>
              Qty
            </TableCell>
            <TableCell align="right" sx={{ width: 110 }}>
              Rev4 (€)
            </TableCell>
            <TableCell align="right" sx={{ width: 110 }}>
              PVP catálogo (€)
            </TableCell>
            {isAdmin && (
              <TableCell align="right" sx={{ width: 110 }}>
                Custo (€)
              </TableCell>
            )}
            <TableCell sx={{ width: 48 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow key={item._localId}>
              {/* G1 — Tipo select box, before Produto */}
              <TableCell>
                <FormControl size="small" fullWidth variant="standard">
                  <Select
                    value={item.component_type ?? ""}
                    displayEmpty
                    onChange={(e) => {
                      updateItem(item._localId, {
                        component_type: e.target.value || undefined,
                        // Clear product when slot changes so filter applies correctly
                        product_id: "",
                        name: "",
                        rev4_cents: undefined,
                        pvp_cents: 0,
                        cost_cents: 0,
                      });
                      setSearchValues((prev) => ({
                        ...prev,
                        [item._localId]: "",
                      }));
                    }}
                    sx={{ fontSize: 13 }}
                  >
                    <MenuItem value="">
                      <em>— sem tipo —</em>
                    </MenuItem>
                    {Object.keys(COMPONENT_TYPE_TO_ROOT_CATEGORY_ID).map(
                      (ct) => (
                        <MenuItem key={ct} value={ct} sx={{ fontSize: 13 }}>
                          {ct}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>
                <ProductSearchInput
                  value={searchValues[item._localId] ?? item.product_id}
                  onChange={(v) =>
                    setSearchValues((prev) => ({
                      ...prev,
                      [item._localId]: v,
                    }))
                  }
                  onSelect={(p: CatalogProduct) => {
                    updateItem(item._localId, {
                      product_id: p.id,
                      name: p.name,
                      component_type: item.component_type, // slot type stays fixed
                      rev4_cents: Math.round((p.rev4_price ?? 0) * 100),
                      rev3_cents: Math.round((p.rev3_price ?? 0) * 100),
                      pvp_cents: Math.round(
                        (p.pvp_price ?? p.rev4_price ?? 0) * 100,
                      ),
                      cost_cents: Math.round(p.cost_price * 100),
                      stock: p.stock ?? undefined,
                    });
                    setSearchValues((prev) => ({
                      ...prev,
                      [item._localId]: p.id,
                    }));
                  }}
                  componentType={item.component_type}
                  placeholder="Ref. produto…"
                />
                {item.name && (
                  <Typography variant="caption" color="text.secondary">
                    {item.name}
                  </Typography>
                )}
                {item.product_id &&
                  item.stock !== undefined &&
                  item.stock === 0 && (
                    <Tooltip title="Produto sem stock disponível no catálogo">
                      <Chip
                        label="Sem stock"
                        size="small"
                        role="img"
                        aria-label="Produto sem stock disponível"
                        data-testid={`no-stock-${item.product_id}`}
                        sx={{
                          bgcolor: "#DC2626",
                          color: "white",
                          fontWeight: 600,
                          height: 18,
                          fontSize: 10,
                        }}
                      />
                    </Tooltip>
                  )}
                {item.product_id &&
                  item.rev3_cents !== undefined &&
                  item.rev3_cents === 0 && (
                    <Tooltip title="Este produto não tem preço Rev3 definido no catálogo">
                      <Chip
                        label="Sem Rev3"
                        size="small"
                        role="img"
                        aria-label="Produto sem preço Rev3 definido no catálogo"
                        data-testid={`no-rev3-${item.product_id}`}
                        sx={{
                          bgcolor: "#D97706",
                          color: "white",
                          fontWeight: 600,
                          height: 18,
                          fontSize: 10,
                          ml: 0.5,
                        }}
                      />
                    </Tooltip>
                  )}
                {driftItems &&
                  driftItems.some((d) => d.compound_id === item.product_id) && (
                    <DriftChip
                      severity={
                        driftItems.find(
                          (d) => d.compound_id === item.product_id,
                        )?.severity
                      }
                      onClick={() => onDriftClick?.()}
                    />
                  )}
              </TableCell>
              <TableCell align="right">
                <TextField
                  type="number"
                  size="small"
                  value={item.quantity}
                  inputProps={{
                    min: 1,
                    style: { textAlign: "right", width: 50 },
                  }}
                  onChange={(e) =>
                    updateItem(item._localId, {
                      quantity: Math.max(1, parseInt(e.target.value, 10) || 1),
                    })
                  }
                  variant="standard"
                />
              </TableCell>
              {/* G3 — Rev4 read-only reference */}
              <TableCell align="right" sx={{ color: "text.secondary" }}>
                <Tooltip title="Preço Rev4 de catálogo (referência, só leitura)">
                  <span>
                    {item.rev4_cents != null
                      ? formatCents(item.rev4_cents)
                      : "—"}
                  </span>
                </Tooltip>
              </TableCell>
              {/* G3 — PVP catálogo read-only reference */}
              <TableCell align="right" sx={{ color: "text.secondary" }}>
                <Tooltip title="PVP de catálogo (referência, só leitura). O preço final da proposta é definido no painel abaixo.">
                  <span>
                    {item.pvp_cents > 0 ? formatCents(item.pvp_cents) : "—"}
                  </span>
                </Tooltip>
              </TableCell>
              {isAdmin && (
                <TableCell align="right" sx={{ color: "text.secondary" }}>
                  <Tooltip title="Custo de catálogo (só leitura)">
                    <span>{formatCents(item.cost_cents)} €</span>
                  </Tooltip>
                </TableCell>
              )}
              <TableCell>
                <IconButton
                  size="small"
                  onClick={() => removeItem(item._localId)}
                  disabled={items.length === 1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow sx={{ "& td": { fontWeight: 700, bgcolor: "#f5f5f5" } }}>
            <TableCell sx={{ width: 160 }} colSpan={3}>
              Total
            </TableCell>
            <TableCell align="right" sx={{ width: 110 }}>
              {formatCents(totalRev4)}
            </TableCell>
            <TableCell align="right" sx={{ width: 110 }}>
              {formatCents(totalPvp)}
            </TableCell>
            {isAdmin && (
              <TableCell align="right" sx={{ width: 110 }}>
                {formatCents(totalCost)}
              </TableCell>
            )}
            <TableCell sx={{ width: 48 }} />
          </TableRow>
        </TableFooter>
      </Table>

      {hasEmptyRow && (
        <Box
          sx={{
            bgcolor: "#fff3e0",
            border: "1px solid #ffb74d",
            borderRadius: 1,
            px: 1.5,
            py: 0.5,
            mb: 1,
          }}
        >
          <Typography variant="caption" color="warning.main">
            ⚠ Linhas sem produto serão ignoradas ao guardar.
          </Typography>
        </Box>
      )}
      <Box display="flex" gap={1} mb={2}>
        <Button
          startIcon={<AddIcon />}
          size="small"
          variant="outlined"
          onClick={addItem}
        >
          Adicionar linha
        </Button>
      </Box>
    </>
  );
};

export default ProposalItemsTable;
